import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './services/queryClient';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import './utils/i18n';

// Auth
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import RestrictedRoute from './components/auth/RestrictedRoute';
import RoleBasedRedirect from './components/auth/RoleBasedRedirect';

// Analytics
import { AnalyticsProvider } from './contexts/AnalyticsContext';
import { GoogleAnalytics, ButtonTracker } from './analytics';
import { CookieConsent } from './components/common/CookieConsent';
import TesterFeedbackBubble from './components/common/TesterFeedbackBubble';

// Layout
import MainLayout from './components/layout/MainLayout';
import PublicLayout from './components/layout/PublicLayout';

// Pages
import Landing from './pages/Landing';
import Login from './pages/auth/Login';
import SignUp from './pages/auth/SignUp';
import Register from './pages/auth/Register';
import AuthCallback from './pages/auth/AuthCallback';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import AuthParamsRedirect from './components/auth/AuthParamsRedirect';
import LoadingSpinner from './components/common/LoadingSpinner';

// Lazy-loaded pages (code-split for smaller initial bundle)
// lazyWithRetry: retries failed chunk loads (e.g. after a deploy changes hashes)
// and forces a full page reload on persistent failure.
function lazyWithRetry<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  retries = 2,
): React.LazyExoticComponent<T> {
  return React.lazy(() => retryImport(factory, retries));
}

function retryImport<T>(
  factory: () => Promise<T>,
  retries: number,
): Promise<T> {
  return factory().catch((error: Error) => {
    if (retries > 0) {
      return new Promise<T>((resolve) =>
        setTimeout(() => resolve(retryImport(factory, retries - 1)), 1000),
      );
    }
    // All retries exhausted — force reload (but only once to avoid infinite loop)
    const reloadedKey = 'chunk_reload_' + window.location.pathname;
    if (!sessionStorage.getItem(reloadedKey)) {
      sessionStorage.setItem(reloadedKey, '1');
      window.location.reload();
    }
    throw error;
  });
}

// Marketing Pages — Features, Pricing, FAQ, and About are currently hidden;
// their routes redirect to home. Re-add the lazy imports here to re-enable them.

// Legal Pages
const TermsOfService = lazyWithRetry(() => import('./pages/legal/TermsOfService'));
const PrivacyPolicy = lazyWithRetry(() => import('./pages/legal/PrivacyPolicy'));

// Core Pages
const Dashboard = lazyWithRetry(() => import('./pages/Dashboard'));
const Clients = lazyWithRetry(() => import('./pages/clients/Clients'));
const CaseCreate = lazyWithRetry(() => import('./pages/cases/CaseCreate'));
const CaseEdit = lazyWithRetry(() => import('./pages/cases/CaseEdit'));
const CaseDetail = lazyWithRetry(() => import('./pages/cases/CaseDetail'));
const ArchivedCases = lazyWithRetry(() => import('./pages/cases/ArchivedCases'));
const AccountingDashboard = lazyWithRetry(() => import('./pages/accounting/AccountingDashboard'));
const Plazos = lazyWithRetry(() => import('./pages/plazos/Plazos'));
const FixedCosts = lazyWithRetry(() => import('./pages/providers/FixedCosts'));
const VariableCosts = lazyWithRetry(() => import('./pages/providers/VariableCosts'));

// Settings Pages
const PrivacySettings = lazyWithRetry(() => import('./pages/settings/PrivacySettings'));
const SecuritySettings = lazyWithRetry(() => import('./pages/settings/SecuritySettings'));
const MFASetup = lazyWithRetry(() => import('./pages/settings/MFASetup'));

// Admin Pages
const SubscriptionPage = lazyWithRetry(() => import('./pages/admin/SubscriptionPage'));
const AuditLogs = lazyWithRetry(() => import('./pages/admin/AuditLogs'));
const SecurityDashboard = lazyWithRetry(() => import('./pages/admin/SecurityDashboard'));
const DataRetention = lazyWithRetry(() => import('./pages/admin/DataRetention'));
const TesterFeedbackPage = lazyWithRetry(() => import('./pages/admin/TesterFeedbackPage'));

// Superadmin Pages
const SuperadminDashboard = lazyWithRetry(() => import('./pages/superadmin/Dashboard'));
const AdminManagement = lazyWithRetry(() => import('./pages/superadmin/AdminManagement'));
const OrganizationManagement = lazyWithRetry(() => import('./pages/superadmin/OrganizationManagement'));
const SystemMonitoring = lazyWithRetry(() => import('./pages/superadmin/SystemMonitoring'));
const PlanConfiguration = lazyWithRetry(() => import('./pages/superadmin/PlanConfiguration'));
const TesterAccountManagement = lazyWithRetry(() => import('./pages/superadmin/TesterAccountManagement'));
const FeatureWaitlistPage = lazyWithRetry(() => import('./pages/superadmin/FeatureWaitlist'));
const SuperadminAuditLogs = lazyWithRetry(() => import('./pages/superadmin/SuperadminAuditLogs'));

// Auth Pages (lazy - not in critical auth path)
const AcceptInvitation = React.lazy(() => import('./pages/auth/AcceptInvitation'));
const ActivateTesterAccount = React.lazy(() => import('./pages/auth/ActivateTesterAccount'));

// Create Material-UI theme with cursor.com light theme style
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#000000',
      light: '#333333',
      dark: '#000000',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#666666',
      light: '#888888',
      dark: '#444444',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    text: {
      primary: '#000000',
      secondary: '#666666',
    },
    divider: 'rgba(0, 0, 0, 0.08)',
    action: {
      hover: 'rgba(0, 0, 0, 0.04)',
      selected: 'rgba(0, 0, 0, 0.08)',
    },
  },
  typography: {
    fontFamily: [
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontWeight: 700,
      fontSize: '3.5rem',
      lineHeight: 1.1,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 700,
      fontSize: '2.5rem',
      lineHeight: 1.2,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontWeight: 600,
      fontSize: '2rem',
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.4,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
      color: '#000000',
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
      color: '#666666',
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
      letterSpacing: '0.01em',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 20px',
          fontWeight: 500,
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          backgroundColor: '#000000',
          color: '#ffffff',
          '&:hover': {
            backgroundColor: '#333333',
          },
        },
        outlined: {
          borderColor: 'rgba(0, 0, 0, 0.2)',
          color: '#000000',
          '&:hover': {
            borderColor: 'rgba(0, 0, 0, 0.4)',
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        },
        text: {
          color: '#000000',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          backgroundImage: 'none',
          border: '1px solid rgba(0, 0, 0, 0.08)',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#ffffff',
            '& fieldset': {
              borderColor: 'rgba(0, 0, 0, 0.15)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(0, 0, 0, 0.25)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#000000',
            },
            '& input': {
              color: '#000000',
            },
            '& textarea': {
              color: '#000000',
            },
          },
          '& .MuiInputLabel-root': {
            color: '#666666',
            '&.Mui-focused': {
              color: '#000000',
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0, 0, 0, 0.15)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0, 0, 0, 0.25)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#000000',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          boxShadow: 'none',
          color: '#000000',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: '#ffffff',
          border: '1px solid rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#ffffff',
          border: '1px solid rgba(0, 0, 0, 0.08)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.12)',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(0, 0, 0, 0.06)',
          color: '#000000',
          border: '1px solid rgba(0, 0, 0, 0.1)',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          border: '1px solid rgba(0, 0, 0, 0.08)',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: '#000000',
        },
      },
    },
  },
});

// TODO: Uncomment when connecting Supabase authentication
// Component that uses useAuth hook (must be inside AuthProvider)
// const LandingOrRedirect: React.FC = () => {
//   // Log immediately to verify component is being called
//   console.log('[LandingOrRedirect] Component starting to render');
//   
//   // Hooks must be called at the top level, not conditionally
//   const { isAuthenticated, isLoading } = useAuth();

//   // Debug logging
//   console.log('[LandingOrRedirect] isLoading:', isLoading, 'isAuthenticated:', isAuthenticated);

//   // Show loading spinner while checking auth state
//   if (isLoading) {
//     console.log('[LandingOrRedirect] Showing loading spinner');
//     return (
//       <Box
//         sx={{
//           display: 'flex',
//           justifyContent: 'center',
//           alignItems: 'center',
//           minHeight: '100vh',
//           backgroundColor: '#ffffff',
//         }}
//       >
//         <CircularProgress />
//       </Box>
//     );
//   }

//   // If authenticated, redirect based on role
//   if (isAuthenticated) {
//     console.log('[LandingOrRedirect] User authenticated, redirecting...');
//     return <RoleBasedRedirect />;
//   }

//   // Show landing page for unauthenticated users
//   console.log('[LandingOrRedirect] Rendering Landing page');
//   return <Landing />;
// };

function App() {
  // Suppress AbortError from React Query / fetch cancellation so the runtime overlay doesn't show.
  // These are benign (query cancelled on unmount, Strict Mode, etc.).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const isAbortError = (err: unknown) =>
      (err instanceof DOMException && err.name === 'AbortError') ||
      (err && typeof err === 'object' && (err as { name?: string }).name === 'AbortError') ||
      (err && typeof err === 'object' && String((err as { message?: string }).message || '').includes('signal is aborted'));
    const onError = (event: ErrorEvent) => {
      if (isAbortError(event.error)) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      console.error('[App] Global error:', event.error);
    };
    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (isAbortError(event.reason)) {
        event.preventDefault();
        return;
      }
      console.error('[App] Unhandled promise rejection:', event.reason);
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
          <CssBaseline />
          <AuthProvider>
            <AnalyticsProvider>
              <BrowserRouter>
                <GoogleAnalytics />
                <ButtonTracker />
                <AuthParamsRedirect />
                <Suspense fallback={<LoadingSpinner />}>
                <Routes>
                {/* Public Marketing Routes - Wrapped in PublicLayout */}
                <Route
                  element={<PublicLayout />}
                >
                  <Route path="/" element={<Landing />} />
                  <Route path="/pricing" element={<Navigate to="/" replace />} />
                  <Route path="/features" element={<Navigate to="/" replace />} />
                  <Route path="/faq" element={<Navigate to="/" replace />} />
                  <Route path="/about" element={<Navigate to="/" replace />} />
                  <Route path="/terms" element={<TermsOfService />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                </Route>

                {/* Public Auth Routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<SignUp />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/accept-invitation" element={<AcceptInvitation />} />
                <Route path="/activate-tester" element={<ActivateTesterAccount />} />

                {/* Protected Routes - All routes under MainLayout */}
                <Route
                  element={
                    <ProtectedRoute>
                      <MainLayout />
                    </ProtectedRoute>
                  }
                >
                  {/* Removed index route - use specific paths instead to avoid conflict with public / route */}
                  <Route 
                    path="dashboard" 
                    element={
                      <RestrictedRoute excludedRoles={['superadmin']}>
                        <Dashboard />
                      </RestrictedRoute>
                    } 
                  />
                  <Route 
                    path="clients" 
                    element={
                      <RestrictedRoute excludedRoles={['superadmin']}>
                        <Clients />
                      </RestrictedRoute>
                    } 
                  />
                  <Route 
                    path="cases/create" 
                    element={
                      <RestrictedRoute excludedRoles={['superadmin']}>
                        <CaseCreate />
                      </RestrictedRoute>
                    } 
                  />
                  <Route 
                    path="cases/:id/edit" 
                    element={
                      <RestrictedRoute excludedRoles={['superadmin']}>
                        <CaseEdit />
                      </RestrictedRoute>
                    } 
                  />
                  <Route 
                    path="cases/archived" 
                    element={
                      <RestrictedRoute excludedRoles={['superadmin']}>
                        <ArchivedCases />
                      </RestrictedRoute>
                    } 
                  />
                  <Route 
                    path="cases/:id" 
                    element={
                      <RestrictedRoute excludedRoles={['superadmin']}>
                        <CaseDetail />
                      </RestrictedRoute>
                    } 
                  />
                  <Route path="accounting" element={<AccountingDashboard />} />
                  <Route path="plazos" element={<Plazos />} />
                  <Route
                    path="providers/fixed-costs"
                    element={
                      <RestrictedRoute excludedRoles={['superadmin']}>
                        <ProtectedRoute requiredRole="admin">
                          <FixedCosts />
                        </ProtectedRoute>
                      </RestrictedRoute>
                    }
                  />
                  <Route path="providers/variable-costs" element={<VariableCosts />} />

                  {/* Settings Routes */}
                  <Route path="settings/privacy" element={<PrivacySettings />} />

                  {/* Admin Routes - Suscripción (Usuarios + Invitaciones + Plan) */}
                  <Route
                    path="admin/subscription"
                    element={
                      <RestrictedRoute excludedRoles={['superadmin']}>
                        <ProtectedRoute requiredRole="admin">
                          <SubscriptionPage />
                        </ProtectedRoute>
                      </RestrictedRoute>
                    }
                  />
                  <Route path="admin/users" element={<Navigate to="/admin/subscription" replace />} />
                  <Route path="admin/invitations" element={<Navigate to="/admin/subscription" replace />} />

                  {/* Settings Routes */}
                  <Route path="settings/security" element={<SecuritySettings />} />
                  <Route path="settings/mfa-setup" element={<MFASetup />} />

                  {/* Superadmin Routes */}
                  <Route
                    path="superadmin"
                    element={
                      <ProtectedRoute requiredRole="superadmin">
                        <SuperadminDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="superadmin/admins"
                    element={
                      <ProtectedRoute requiredRole="superadmin">
                        <AdminManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="superadmin/organizations"
                    element={
                      <ProtectedRoute requiredRole="superadmin">
                        <OrganizationManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="superadmin/monitoring"
                    element={
                      <ProtectedRoute requiredRole="superadmin">
                        <SystemMonitoring />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="superadmin/plans"
                    element={
                      <ProtectedRoute requiredRole="superadmin">
                        <PlanConfiguration />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="superadmin/tester-accounts"
                    element={
                      <ProtectedRoute requiredRole="superadmin">
                        <TesterAccountManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="superadmin/audit-logs"
                    element={
                      <ProtectedRoute requiredRole="superadmin">
                        <SuperadminAuditLogs />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="superadmin/data-retention"
                    element={
                      <ProtectedRoute requiredRole="superadmin">
                        <DataRetention />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="superadmin/security-dashboard"
                    element={
                      <ProtectedRoute requiredRole="superadmin">
                        <SecurityDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="superadmin/tester-feedback"
                    element={
                      <ProtectedRoute requiredRole="superadmin">
                        <TesterFeedbackPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="superadmin/feature-waitlist"
                    element={
                      <ProtectedRoute requiredRole="superadmin">
                        <FeatureWaitlistPage />
                      </ProtectedRoute>
                    }
                  />
                </Route>

                {/* Catch all - redirect based on role */}
                <Route path="*" element={<RoleBasedRedirect />} />
              </Routes>
              </Suspense>
              <CookieConsent />
              <TesterFeedbackBubble />
            </BrowserRouter>
            </AnalyticsProvider>
          </AuthProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
